/* eslint-disable no-param-reassign */
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import produce from 'immer';
import moment from 'moment';
import { showNotification } from '../redux/notification';
import utils from '../utils/utils';

const INITIAL_SHIFT_GROUPS = [
	{
		resourceType: null,
		shiftStructureId: null,
		unitId: null,
		unitName: '',
		numberOfProviders: 1,
		isGuaranteed: false,
		id: `id-${Math.random().toString()}`,
		dates: []
	}
];

const BACKEND_FIELDS_TO_ERROR_KEY_MAP = {
	is_disabled: 'shiftStructureId',
	structure_id: 'shiftStructureId',
	slots: 'numberOfProviders'
};

const getShiftsToRequest = (shiftsGroups, isFlex) => {
	const shifts = [];
	shiftsGroups.forEach((shift) => {
		const dataObj = {
			structure_id: shift.shiftStructureId,
			slots: shift.numberOfProviders,
			unit_id: shift.unitId
		};
		if (!isFlex) {
			dataObj.is_guaranteed = shift.isGuaranteed;
			dataObj.portalShiftId = shift.id;
		}
		shift.dates.forEach((shiftDate) => {
			shifts.push({ ...dataObj, start_date: moment(shiftDate).format('YYYY-MM-DD') });
		});
	});
	return shifts;
};

const createNewShift = () => {
	const newShift = { ...INITIAL_SHIFT_GROUPS[0] };
	newShift.id = `id-${Math.random().toString()}`;
	return newShift;
};

const useRequestShifts = (userData, currentShiftStructures) => {
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showFlexStructuresAlreadyStartedModal, setShowFlexStructuresAlreadyStartedModal] = useState(false);
	const [shiftsGroups, setShiftsGroups] = useState(INITIAL_SHIFT_GROUPS);
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();

	const addEmptyShiftGroup = () => {
		if (shiftsGroups.length >= Number(userData.maxShiftsToCreate)) {
			dispatch(
				showNotification(
					'error',
					`Sorry, you can only post ${userData.maxShiftsToCreate} different shifts per shift request.`
				)
			);
			return;
		}
		const nextState = produce(shiftsGroups, (draftState) => {
			draftState.push(createNewShift());
		});
		setShiftsGroups(nextState);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const clearErrors = (updatedObject, shiftId) => {
		const newErrors = { ...errors };
		Object.keys(updatedObject).forEach((key) => {
			if (key === 'shiftStructureId' && updatedObject[key] === null) return;
			if (key === 'unitId' && updatedObject[key] === null) return;
			if (key === 'invalidDate') {
				newErrors[shiftId] = {
					...newErrors[shiftId],
					[key]: updatedObject[key]
				};
				return;
			}

			if (typeof updatedObject[key] !== 'undefined') {
				newErrors[shiftId] = {
					...newErrors[shiftId],
					[key]: !updatedObject[key]
				};
			}
		});
		setErrors(newErrors);
	};

	const removeShiftError = useCallback(
		(shiftId) => {
			const newErrors = produce(errors, (draft) => {
				delete draft[shiftId];
			});
			setErrors(newErrors);
		},
		[errors]
	);

	const updateShift = (shiftIndex, patchObject, shiftId) => {
		const nextState = produce(shiftsGroups, (draftState) => {
			const shift = draftState[shiftIndex];
			Object.keys(shift).forEach((key) => {
				if (typeof patchObject[key] !== 'undefined') {
					shift[key] = patchObject[key];
				}
			});
		});
		clearErrors(patchObject, shiftId);
		setShiftsGroups(nextState);
	};

	const removeShift = (shiftIndex, shiftGeneralIndex) => {
		const nextState = produce(shiftsGroups, (draftState) => {
			draftState.splice(shiftIndex, 1);
		});
		removeShiftError(shiftGeneralIndex);
		setShiftsGroups(nextState);
	};

	const checkIfStructuresAreNearToEnd = (shiftStructuresData) => {
		const structures = utils.groupDataByKey(currentShiftStructures, 'id');
		return shiftStructuresData.some((data) => {
			const [currentStructure] = structures[data.structureId];
			return utils.isStructureEndingSoon(currentStructure, userData.timezone, data.date);
		});
	};

	const checkIfStructuresAlreadyStarted = (shiftStructuresData) => {
		const structures = utils.groupDataByKey(currentShiftStructures, 'id');
		return shiftStructuresData.some((data) => {
			const [currentStructure] = structures[data.structureId];
			return utils.isStructureAlreadyStarted(currentStructure, userData.timezone, data.date);
		});
	};

	const checkIntegrity = async (isFlex) => {
		const newErrors = {};
		const shiftStructuresIds = [];
		shiftsGroups.forEach((shift) => {
			shiftStructuresIds.push({ structureId: shift.shiftStructureId, date: shift.dates[0] });
			if (
				!shift.shiftStructureId ||
				!shift.numberOfProviders ||
				!shift.dates.length ||
				!shift.numberOfProviders ||
				shift.numberOfProviders > 20
			) {
				newErrors[shift.id] = {
					resourceType: !shift.resourceType,
					shiftStructureId: !shift.shiftStructureId,
					numberOfProviders: !shift.numberOfProviders || shift.numberOfProviders > 20,
					dates: !shift.dates?.length
				};
			}
			if (shift.shiftStructureId) {
				if (
					(isFlex && checkIfStructuresAreNearToEnd([{ structureId: shift.shiftStructureId, date: shift.dates[0] }])) ||
					(!isFlex && checkIfStructuresAlreadyStarted([{ structureId: shift.shiftStructureId, date: shift.dates[0] }]))
				) {
					newErrors[shift.id] = {
						...newErrors[shift.id],
						invalidDate: true
					};
				}
			}
		});
		const errorKeys = Object.keys(newErrors).length;
		if (errorKeys) {
			dispatch(showNotification('error', 'Please complete all fields.'));
		} else if (isFlex && checkIfStructuresAreNearToEnd(shiftStructuresIds)) {
			dispatch(
				showNotification(
					'error',
					'One or more of the requested shifts has ended. Please update your request and try again.'
				)
			);
		} else if (isFlex && checkIfStructuresAlreadyStarted(shiftStructuresIds)) {
			setShowFlexStructuresAlreadyStartedModal(true);
		} else {
			setShowConfirmationModal(true);
		}

		setErrors(newErrors);
	};

	const checkIfCanAddTodayShiftForStructure = (shiftStructureId, isFlex) =>
		isFlex
			? !checkIfStructuresAreNearToEnd([{ structureId: shiftStructureId, date: new Date() }])
			: !checkIfStructuresAlreadyStarted([{ structureId: shiftStructureId, date: new Date() }]);

	const handleBackendErrors = (backendErrors) => {
		const newErrors = {};
		if (Array.isArray(backendErrors) && backendErrors.length > 0) {
			backendErrors.forEach((error) => {
				newErrors[error.portalShiftId] = {
					[BACKEND_FIELDS_TO_ERROR_KEY_MAP[error.field]]: true
				};
			});
		}

		setErrors(newErrors);
	};

	const reset = () => setShiftsGroups(INITIAL_SHIFT_GROUPS);

	return {
		shiftsGroups,
		errors,
		addEmptyShiftGroup,
		updateShift,
		removeShift,
		setShiftsGroups,
		setErrors,
		checkIntegrity,
		checkIfCanAddTodayShiftForStructure,
		showConfirmationModal,
		setShowConfirmationModal,
		showFlexStructuresAlreadyStartedModal,
		setShowFlexStructuresAlreadyStartedModal,
		handleBackendErrors,
		initialShiftGroups: INITIAL_SHIFT_GROUPS,
		getShiftsToRequest,
		reset
	};
};

export const computeTotalShiftsToCreate = (shiftsGroups) => {
	let count = 0;
	shiftsGroups.forEach((shiftGroup) => {
		count += shiftGroup.dates.length || 0;
	});
	return count;
};

export default useRequestShifts;
